import { useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface SubItem {
  name: string;
  href: string;
  // Marca activo SOLO con la ruta exacta. Hace falta cuando otro ítem del grupo
  // cuelga por debajo: /gestion/alianzas empieza con /gestion, así que sin esto
  // los dos se pintaban activos a la vez.
  exacta?: boolean;
}

interface SidebarGroupProps {
  id: string;
  label: string;
  icon: React.ReactNode;
  items: SubItem[];
  isOpen: boolean;
  collapsed: boolean;
  onToggle: () => void;
}

const ChevronDown = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

export default function SidebarGroup({
  label,
  icon,
  items,
  isOpen,
  collapsed,
  onToggle,
}: SidebarGroupProps) {
  const location = useLocation();
  const btnRef = useRef<HTMLButtonElement>(null);
  const [popoverTop, setPopoverTop] = useState<number | null>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const esActivo = (item: SubItem) =>
    item.exacta
      ? location.pathname === item.href
      : location.pathname === item.href || location.pathname.startsWith(item.href + '/');

  const isChildActive = items.some(esActivo);

  const handleMouseEnter = () => {
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPopoverTop(rect.top);
    }
  };
  
  const handleMouseLeave = () => {
    hideTimeoutRef.current = setTimeout(() => setPopoverTop(null), 100);
  };

  if (collapsed) {
    return (
      <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        <button
          ref={btnRef}
          className={`w-full flex items-center justify-center p-3 rounded-md transition ${
            isChildActive
              ? 'bg-primary-800 text-white'
              : 'text-primary-100 hover:bg-primary-600 hover:text-white'
          }`}
        >
          {icon}
        </button>

        {popoverTop !== null && (
          <div
            style={{ position: 'fixed', top: popoverTop, left: 64, zIndex: 9999 }}
            className="bg-white rounded-md shadow-lg border border-gray-200 py-1 min-w-[160px]"
            onMouseEnter={() => { if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current); }}
            onMouseLeave={() => setPopoverTop(null)}
          >
            <p className="px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-100">
              {label}
            </p>
            {items.map(item => (
              <Link
                key={item.href}
                to={item.href}
                className={`block px-3 py-2 text-sm transition ${
                  esActivo(item)
                    ? 'bg-primary-50 text-primary-700 font-medium'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <button
        onClick={onToggle}
        className={`w-full group flex items-center gap-x-3 rounded-md p-3 text-sm font-medium transition ${
          isChildActive
            ? 'bg-primary-800 text-white'
            : 'text-primary-100 hover:bg-primary-600 hover:text-white'
        }`}
      >
        {icon}
        <span className="flex-1 text-left">{label}</span>
        <span className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>
          <ChevronDown />
        </span>
      </button>

      <div className={`overflow-hidden transition-all duration-200 ${isOpen ? 'max-h-96' : 'max-h-0'}`}>
        <div className="pl-9 mt-1 flex flex-col gap-y-1">
          {items.map(item => (
            <Link
              key={item.href}
              to={item.href}
              className={`block rounded-md px-3 py-2 text-sm transition ${
                esActivo(item)
                  ? 'bg-primary-800 text-white'
                  : 'text-primary-100 hover:bg-primary-600 hover:text-white'
              }`}
            >
              {item.name}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
